import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TILES as INTEGRATION_TILES } from '../sections/Integrations.jsx';
import { FEATURES as FEATURE_DATA } from '../data/features.js';
import { buildCtaUrl } from '../utils/attribution.js';
import { demoLinkProps } from '../utils/calendly.js';

const APP_STORE = 'https://apps.shopify.com/account-editor';

// Icon, name and href come from the one registry so they can never drift out
// of sync with the feature pages — a hand-copied icon name here once shipped
// as a blank square. Only the nav-length blurb is local, since the mega-menu
// has far less room than a feature page.
const NAV_DESC = {
  'self-serve-edits': 'Sizes, colors, quantities, variants, addresses — all editable from the order confirmation.',
  'cancel-deflection': 'Offer store credit, free upgrades, or smart alternatives the moment a customer hits "cancel".',
  'post-purchase-upsells': 'Land upsells after checkout but before the warehouse picks.',
  'operations-analytics': 'Edit volume, deflection rate, recovered revenue, AOV lift — all in one dashboard.',
  'address-validation': 'Google-validated address corrections on every order.',
};

const FEATURES = FEATURE_DATA.map((f) => ({
  icon: f.icon,
  name: f.label,
  href: `/features/${f.slug}`,
  desc: NAV_DESC[f.slug] || f.desc,
}));

const RESOURCES_LEARN = [
  { label: 'Help Center', href: '/help', desc: 'Guides, setup & troubleshooting' },
  { label: 'Blog', href: '/blog', desc: 'Playbooks & teardowns from operators' },
  { label: 'Setup Guide', href: '/setup-guide', desc: 'Install in 5 minutes — no code' },
  { label: 'Glossary', href: '/glossary', desc: 'Post-purchase terms, defined' },
  { label: 'FAQs', href: '/faqs', desc: 'Install, billing, integrations' },
  { label: 'Changelog', href: '/changelog', desc: "What's new in Account Editor" },
];

const RESOURCES_HIGHLIGHTS = [
  { title: 'ROI Calculator', href: '/roi-calculator', img: '/images/roi-calculator.webp' },
  { title: 'Case Studies', href: '/case-studies', img: '/images/case-studies.webp' },
];

const RESOURCES_COMPARE = [
  { label: 'vs Native Shopify', href: '/compare/account-editor-vs-native-shopify' },
  { label: 'Alternatives', href: '/alternatives' },
];

const RESOURCES_CONNECT = [
  { label: 'Contact Sales', href: '/contact-sales' },
  { label: 'Contact Support', href: '/contact' },
  { label: 'Security', href: '/security' },
  { label: 'About', href: '/about' },
];

export default function Header() {
  const [menu, setMenu] = useState(null); // 'features' | 'resources' | null
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const closeTimer = useRef(null);
  const lastY = useRef(0);

  // hide on scroll down, reveal on scroll up
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const y = window.scrollY || 0;
        if (y < 80) setHidden(false);
        else if (y > lastY.current + 5) setHidden(true);
        else if (y < lastY.current - 5) setHidden(false);
        lastY.current = y;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMenu(null);
  };

  const openMenu = (name) => {
    clearTimeout(closeTimer.current);
    setMenu(name);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMenu(null), 200);
  };

  return (
    <div className="aehome ae-contents">
      <header className={`navbar${hidden ? ' navbar--hidden' : ''}`}>
        <div className="navbar__container">
          <a href="/" className="navbar__logo" aria-label="Account Editor home">
            <img src="/images/logo.webp" alt="Account Editor logo" className="navbar__logo-img" />
          </a>

          <nav className="navbar__links">
            {/* Features mega menu */}
            <div
              className="navbar__item"
              onMouseEnter={() => openMenu('features')}
              onMouseLeave={scheduleClose}
            >
              <button
                className="navbar__link"
                onClick={() => setMenu(menu === 'features' ? null : 'features')}
              >
                Features <i className="ph ph-caret-down navbar__arrow" aria-hidden="true" />
              </button>

              <div className={`megamenu${menu === 'features' ? ' is-open' : ''}`}>
                <div className="megamenu__container">
                  <div className="megamenu__col megamenu__col--features">
                    <span className="megamenu__label">Our Features</span>
                    <ul className="megamenu__list">
                      {FEATURES.map((f) => (
                        <li key={f.name}>
                          <a href={f.href} className="megamenu__feature">
                            <span className="megamenu__icon"><i className={`ph ${f.icon}`} aria-hidden="true" /></span>
                            <span>
                              <span className="megamenu__name">{f.name}</span>
                              <span className="megamenu__desc">{f.desc}</span>
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="megamenu__col megamenu__col--integrations">
                    <span className="megamenu__label">Integrations</span>
                    <div className="megamenu__logos">
                      {INTEGRATION_TILES.map((t) => (
                        <a key={t.alt} href={`/help/${t.slug}`} className="megamenu__logo">
                          <img src={t.src} alt={t.alt} loading="lazy" />
                        </a>
                      ))}
                    </div>
                    <a href="/integrations" className="link-arrow">
                      View all integrations <i className="ph ph-arrow-right" aria-hidden="true" />
                    </a>
                  </div>

                  <div className="megamenu__col megamenu__col--featured">
                    <span className="megamenu__label">Featured</span>
                    <a href="/features" className="megamenu__promo">
                      <span className="megamenu__promo-img"><img src="/images/all-features.webp" alt="All Features" /></span>
                      <span className="megamenu__promo-title">Explore every feature</span>
                      <span className="megamenu__promo-text">Five pillars covering the entire post-purchase moment.</span>
                      <span className="link-arrow">See all features <i className="ph ph-arrow-right" aria-hidden="true" /></span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <a href="/pricing" className="navbar__link">Pricing</a>
            <a href="/case-studies" className="navbar__link">Customers</a>
            <a href="/integrations" className="navbar__link">Integrations</a>

            {/* Resources mega menu */}
            <div
              className="navbar__item"
              onMouseEnter={() => openMenu('resources')}
              onMouseLeave={scheduleClose}
            >
              <button
                className="navbar__link"
                onClick={() => setMenu(menu === 'resources' ? null : 'resources')}
              >
                Resources <i className="ph ph-caret-down navbar__arrow" aria-hidden="true" />
              </button>

              <div className={`megamenu megamenu--resources${menu === 'resources' ? ' is-open' : ''}`}>
                <div className="megamenu__container">
                  <div className="megamenu__col megamenu__col--learn">
                    <span className="megamenu__label">Learn</span>
                    <ul className="megamenu__list">
                      {RESOURCES_LEARN.map((r) => (
                        <li key={r.label}>
                          <a href={r.href} className="megamenu__feature megamenu__feature--text">
                            <span>
                              <span className="megamenu__name">{r.label}</span>
                              <span className="megamenu__desc">{r.desc}</span>
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="megamenu__col megamenu__col--highlights">
                    <span className="megamenu__label">Highlights</span>
                    <div className="megamenu__cards">
                      {RESOURCES_HIGHLIGHTS.map((h) => (
                        <a key={h.title} href={h.href} className="megamenu__card">
                          {/* Decorative: the card title right below carries the name. */}
                          <span className="megamenu__card-img"><img src={h.img} alt="" /></span>
                          <span className="megamenu__card-title">{h.title}</span>
                        </a>
                      ))}
                    </div>
                  </div>

                  <div className="megamenu__col megamenu__col--meta">
                    <div className="megamenu__group">
                      <span className="megamenu__label">Compare</span>
                      <ul className="megamenu__list">
                        {RESOURCES_COMPARE.map((c) => (
                          <li key={c.label}><a href={c.href} className="megamenu__link">{c.label}</a></li>
                        ))}
                      </ul>
                    </div>
                    <div className="megamenu__group">
                      <span className="megamenu__label">Connect</span>
                      <ul className="megamenu__list">
                        {RESOURCES_CONNECT.map((c) => (
                          <li key={c.label}><a href={c.href} className="megamenu__link">{c.label}</a></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </nav>

          <div className="navbar__actions">
            <a {...demoLinkProps('nav-demo')} className="btn btn--outline">Book a 15-min call</a>
            <a href={buildCtaUrl(APP_STORE, 'nav-install')} className="btn btn--primary">
              Install Free on Shopify <i className="ph ph-arrow-up-right" aria-hidden="true" />
            </a>
          </div>

          {/* Mobile-only install CTA. navbar__actions is hidden at <=768px and
              MobileCtaBar only appears after 480px of scroll, so without this
              there is no conversion path above the fold on a phone. */}
          <a
            href={buildCtaUrl(APP_STORE, 'nav-mobile-install')}
            className="btn btn--primary navbar__cta-mobile"
            target="_blank"
            rel="noopener"
          >
            Install Free
          </a>

          <button
            className="navbar__burger"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <i className={`ph ${mobileOpen ? 'ph-x' : 'ph-list'}`} aria-hidden="true" />
          </button>
        </div>
      </header>

      <MobileDrawer open={mobileOpen} onClose={closeMobile} />
    </div>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

function MobileDrawer({ open, onClose }) {
  const [acc, setAcc] = useState(null); // 'features' | 'resources' | null
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  // React 18 can't hydrate a portal: it tries to match the drawer against the
  // header's own prerendered markup, fails, and throws the whole page away to
  // re-render it. Mount the drawer only once hydration is done.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) setAcc(null);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Move focus into the drawer on open and hand it back to the burger on
  // close. Without this the drawer opens with focus still on the button
  // behind the scrim, and closing drops focus to <body>.
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      const first = panelRef.current?.querySelector(FOCUSABLE);
      if (first) first.focus();
      return undefined;
    }
    const trigger = triggerRef.current;
    triggerRef.current = null;
    if (trigger && typeof trigger.focus === 'function') trigger.focus();
    return undefined;
  }, [open]);

  const handleKeyDown = (e) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = panelRef.current.querySelectorAll(FOCUSABLE);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!mounted) return null;

  const toggle = (name) => setAcc((cur) => (cur === name ? null : name));

  // data-client-only: the prerender strips it, so the static HTML doesn't ship
  // a dead copy of the drawer next to the one React mounts.
  return createPortal(
    <div className={`aehome ae-drawer${open ? ' is-open' : ''}`} data-client-only>
      <div className="ae-drawer__scrim" onClick={onClose} aria-hidden="true" />

      <aside
        className="ae-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        ref={panelRef}
        onKeyDown={handleKeyDown}
      >
        <div className="ae-drawer__head">
          <img src="/images/logo.webp" alt="Account Editor" className="ae-drawer__logo" />
          <button
            type="button"
            className="ae-drawer__close"
            aria-label="Close menu"
            onClick={onClose}
          >
            <i className="ph ph-x" aria-hidden="true" />
          </button>
        </div>

        <div className="ae-drawer__body">
          {/* Features accordion */}
          <div className={`ae-drawer__group${acc === 'features' ? ' is-open' : ''}`}>
            <button
              type="button"
              className="ae-drawer__toggle"
              aria-expanded={acc === 'features'}
              onClick={() => toggle('features')}
            >
              Features
              <i className="ph ph-caret-down ae-drawer__chevron" aria-hidden="true" />
            </button>
            <div className="ae-drawer__panel-inner">
              <div className="ae-drawer__panel-content">
                <ul className="ae-drawer__sublist">
                  {FEATURES.map((f) => (
                    <li key={f.name}>
                      <a href={f.href} className="ae-drawer__subitem" onClick={onClose}>
                        <span className="ae-drawer__subicon"><i className={`ph ${f.icon}`} aria-hidden="true" /></span>
                        {f.name}
                      </a>
                    </li>
                  ))}
                  <li>
                    <a href="/features" className="ae-drawer__subitem ae-drawer__subitem--all" onClick={onClose}>
                      See all features <i className="ph ph-arrow-right" aria-hidden="true" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <a href="/pricing" className="ae-drawer__link" onClick={onClose}>Pricing</a>
          <a href="/case-studies" className="ae-drawer__link" onClick={onClose}>Customers</a>
          <a href="/integrations" className="ae-drawer__link" onClick={onClose}>Integrations</a>

          {/* Resources accordion */}
          <div className={`ae-drawer__group${acc === 'resources' ? ' is-open' : ''}`}>
            <button
              type="button"
              className="ae-drawer__toggle"
              aria-expanded={acc === 'resources'}
              onClick={() => toggle('resources')}
            >
              Resources
              <i className="ph ph-caret-down ae-drawer__chevron" aria-hidden="true" />
            </button>
            <div className="ae-drawer__panel-inner">
              <div className="ae-drawer__panel-content">
                <span className="ae-drawer__sublabel">Learn</span>
                <ul className="ae-drawer__sublist">
                  {RESOURCES_LEARN.map((r) => (
                    <li key={r.label}>
                      <a href={r.href} className="ae-drawer__subitem" onClick={onClose}>{r.label}</a>
                    </li>
                  ))}
                </ul>
                <span className="ae-drawer__sublabel">Compare</span>
                <ul className="ae-drawer__sublist">
                  {RESOURCES_COMPARE.map((c) => (
                    <li key={c.label}>
                      <a href={c.href} className="ae-drawer__subitem" onClick={onClose}>{c.label}</a>
                    </li>
                  ))}
                </ul>
                <span className="ae-drawer__sublabel">Connect</span>
                <ul className="ae-drawer__sublist">
                  {RESOURCES_CONNECT.map((c) => (
                    <li key={c.label}>
                      <a href={c.href} className="ae-drawer__subitem" onClick={onClose}>{c.label}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="ae-drawer__cta">
          <a href={buildCtaUrl(APP_STORE, 'nav-install')} className="btn btn--primary" onClick={onClose}>
            Install Free on Shopify <i className="ph ph-arrow-up-right" aria-hidden="true" />
          </a>
          <a {...demoLinkProps('nav-mobile-demo')} className="btn btn--outline" onClick={onClose}>
            Book a 15-min call
          </a>
        </div>
      </aside>
    </div>,
    document.body
  );
}
